import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

const Cart = ({ cart, onUpdateQuantity, onRemoveFromCart }) => {
  const navigate = useNavigate();

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 69 ? 0 : 5.50;
  const total = subtotal + shipping;

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Card className="text-center py-20 border-none shadow-lg">
          <CardContent>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Your cart is empty</h2>
            <p className="text-slate-600 mb-8">Start shopping and add some items to your cart!</p>
            <Link to="/products">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white">
                Continue Shopping
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-bold text-slate-900 mb-8">Shopping Cart</h1>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="md:col-span-2 space-y-4">
          {cart.map((item, index) => (
            <Card key={index} className="border">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {/* Product Image */}
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-32 h-32 object-cover rounded-lg"
                  />

                  {/* Product Details */}
                  <div className="flex-1">
                    <Link to={`/product/${item.id}`}>
                      <h3 className="font-semibold text-slate-900 mb-2 hover:text-slate-700">
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-slate-600 mb-2">{item.subcategory}</p>
                    <div className="flex items-center space-x-4 text-sm text-slate-600 mb-4">
                      {item.selectedSize && <span>Size: {item.selectedSize}</span>}
                      {item.selectedColor && <span>Color: {item.selectedColor}</span>}
                    </div>

                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => onUpdateQuantity(index, Math.max(1, item.quantity - 1))}
                          className="w-8 h-8 border-2 border-slate-200 rounded-lg hover:border-slate-400 transition-colors flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-semibold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                          className="w-8 h-8 border-2 border-slate-200 rounded-lg hover:border-slate-400 transition-colors flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Price and Remove */}
                      <div className="flex items-center space-x-6">
                        <span className="text-xl font-light text-neutral-900">
                          £{(item.price * item.quantity).toFixed(2)}
                        </span>
                        <button
                          onClick={() => onRemoveFromCart(index)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Order Summary */}
        <div>
          <Card className="sticky top-24 border">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-neutral-600">
                  <span>Subtotal</span>
                  <span className="font-light">£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-neutral-600">
                  <span>Shipping</span>
                  <span className="font-light">
                    {shipping === 0 ? 'FREE' : `£${shipping.toFixed(2)}`}
                  </span>
                </div>
                {subtotal < 69 && (
                  <p className="text-sm text-emerald-600">
                    Add £{(69 - subtotal).toFixed(2)} more for free shipping!
                  </p>
                )}
              </div>

              <Separator className="mb-6" />

              <div className="flex justify-between text-xl font-light text-neutral-900 mb-6">
                <span>Total</span>
                <span>£{total.toFixed(2)}</span>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                size="lg"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white mb-4"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <Link to="/products">
                <Button variant="outline" size="lg" className="w-full">
                  Continue Shopping
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Cart;
